// Single per-tab multiplexed SSE provider.
//
// Mount once inside the authenticated layout where orgId is reliably
// available (see _admin.tsx insertion point). All descendant components
// call useRealtime() to read connection status — they never open their own
// EventSource for the shared realtime channel.
//
// FU-1 migration: useMediaJobEvents and usePermissionInvalidationStream are now
// backed by this provider via the dispatch table. useWorkflowRunStream still uses
// its own in-process per-run channel (workflow runner is not broker-backed).
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { startConnectionLoop, realtimeKeyPredicate, MAX_RECONNECTS } from "./connection-loop";
import type { ConnectionHandle } from "./connection-loop";
import type { FrameFamily, RealtimeContextValue, RealtimeStatus } from "./types";

const POLLING_INTERVAL_MS = 5_000;

const RealtimeContext = createContext<RealtimeContextValue>({
  status: "offline",
  lastEventId: 0,
  reconnect: () => undefined,
});

export interface RealtimeProviderProps {
  children: ReactNode;
  /** Active org UUID. When null/empty the provider stays dormant (no connection). */
  orgId: string | null | undefined;
  /** Event families to subscribe to. Mount-time contract; current app uses the default set. */
  families?: FrameFamily[];
}

export const DEFAULT_FAMILIES: FrameFamily[] = [
  "media",
  "kg",
  "kb",
  "bulk",
  "permission",
  "workflow",
];

export function RealtimeProvider({
  children,
  orgId,
  families = DEFAULT_FAMILIES,
}: RealtimeProviderProps) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("offline");
  const [lastEventId, setLastEventId] = useState(0);

  // Refs avoid stale-closure issues in callbacks passed to the connection loop.
  const lastEventIdRef = useRef(0);
  const handleRef = useRef<ConnectionHandle | null>(null);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current !== null) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingTimerRef.current !== null) return; // already running
    pollingTimerRef.current = setInterval(() => {
      qc.invalidateQueries({ predicate: realtimeKeyPredicate });
    }, POLLING_INTERVAL_MS);
  }, [qc]);

  const invalidateAllRealtimeKeys = useCallback(() => {
    qc.invalidateQueries({ predicate: realtimeKeyPredicate });
  }, [qc]);

  const connect = useCallback(() => {
    if (!orgId) return;

    // Tear down any existing connection + polling before reopening.
    handleRef.current?.close();
    stopPolling();
    setStatus("connecting");

    handleRef.current = startConnectionLoop({
      orgId,
      families,
      qc,
      getLastEventId: () => lastEventIdRef.current,
      setLastEventId: (id) => {
        lastEventIdRef.current = id;
        setLastEventId(id);
      },
      onOpen: (prevReconnectCount) => {
        stopPolling();
        setStatus("open");
        // Red-Team H5: on reconnect after a gap, invalidate all realtime
        // keys so DB-truth wins over any stale ring-buffer state.
        if (prevReconnectCount > 0) {
          invalidateAllRealtimeKeys();
        }
      },
      onPollingFallback: () => {
        setStatus("polling");
        startPolling();
        if (import.meta.env.DEV) {
          console.warn(
            `[realtime] SSE failed ${MAX_RECONNECTS} times — switching to polling fallback (${POLLING_INTERVAL_MS}ms).`,
          );
        }
      },
      onStatusChange: (s) => setStatus(s),
    });
  }, [orgId, families, qc, stopPolling, startPolling, invalidateAllRealtimeKeys]);

  // Open/close on orgId change or mount/unmount.
  useEffect(() => {
    // Event IDs are scoped to an org stream. Reset before opening the next
    // org so its URL can never inherit the prior tenant's replay cursor.
    lastEventIdRef.current = 0;
    setLastEventId(0);

    if (!orgId) {
      handleRef.current?.close();
      stopPolling();
      setStatus("offline");
      return;
    }

    connect();

    const onOnline = () => {
      stopPolling();
      connect();
    };
    const onOffline = () => {
      handleRef.current?.close();
      handleRef.current = null;
      setStatus("offline");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      handleRef.current?.close();
      handleRef.current = null;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]); // families is mount-time API; manual reconnect uses latest callback.

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  return (
    <RealtimeContext.Provider value={{ status, lastEventId, reconnect }}>
      {children}
    </RealtimeContext.Provider>
  );
}

/** Returns realtime connection status and a manual reconnect trigger. */
export function useRealtime(): RealtimeContextValue {
  return useContext(RealtimeContext);
}
